import { useEffect, useLayoutEffect, useRef, useState } from 'react';

const FONT_FAMILY =
  "'Inter', 'Poppins', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif";

const DEFAULT_ITEMS = [
  'Vercel',
  'Linear',
  'Stripe',
  'Figma',
  'Notion',
  'Raycast',
  'Arc',
  'Cursor',
];

function useFrame(speed = 1) {
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    let rafId;
    let lastTime = performance.now();

    const tick = (now) => {
      const deltaSec = (now - lastTime) / 1000;
      lastTime = now;
      setFrame((f) => f + deltaSec * 30 * speed);
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [speed]);
  return frame;
}

export function PerspectiveMarquee({
  items = DEFAULT_ITEMS,
  fontSize = 84,
  color = '#fafafa',
  fontWeight = 700,
  pixelsPerFrame = 2,
  rotateY = -28,
  rotateX = 8,
  perspective = 1200,
  fadeColor = '#050505',
  background = '#050505',
  speed = 1,
  className,
  style,
}) {
  const frame = useFrame(speed);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1280);

  useLayoutEffect(() => {
    if (!containerRef.current) return undefined;
    const el = containerRef.current;

    const update = () => setContainerWidth(el.clientWidth || 1280);
    update();

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const itemPadding = fontSize * 0.9;
  const approxItemWidth = items.reduce(
    (acc, item) => acc + item.length * fontSize * 0.6 + itemPadding,
    0
  );

  const offset = -((frame * pixelsPerFrame) % approxItemWidth);
  const rendered = [...items, ...items, ...items];
  const center = containerWidth / 2;

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        position: 'absolute',
        inset: 0,
        background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        perspective: `${perspective}px`,
        ...style,
      }}
    >
      <div
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          transform: `rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
          transformStyle: 'preserve-3d',
        }}
      >
        <div
          style={{
            display: 'flex',
            whiteSpace: 'nowrap',
            transform: `translateX(${offset}px)`,
            willChange: 'transform',
          }}
        >
          {rendered.map((item, i) => {
            const itemCenter =
              i * (approxItemWidth / items.length) +
              approxItemWidth / items.length / 2 +
              offset;
            const norm = center > 0 ? (itemCenter - center) / center : 0;
            const distance = Math.min(1, Math.abs(norm));
            const blurPx = distance * 6;
            const opacity = 1 - distance * 0.4;

            return (
              <span
                key={i}
                style={{
                  display: 'inline-block',
                  fontFamily: FONT_FAMILY,
                  fontSize,
                  fontWeight,
                  color,
                  letterSpacing: '-0.03em',
                  paddingRight: itemPadding,
                  filter: `blur(${blurPx}px)`,
                  opacity,
                }}
              >
                {item}
              </span>
            );
          })}
        </div>
      </div>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: `linear-gradient(90deg, ${fadeColor} 0%, transparent 18%, transparent 82%, ${fadeColor} 100%)`,
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: `linear-gradient(180deg, ${fadeColor} 0%, transparent 25%, transparent 75%, ${fadeColor} 100%)`,
        }}
      />
    </div>
  );
}

export default PerspectiveMarquee;
